





/**
 * This class allows you to add markers to the map. You can specify your own markers
 * or add custom ones.
 *
 * @class TomTomMarkersLayer
 * @extends L.FeatureGroup
 * @namespace L
 * @module Map
 * @constructor
 * @param {Object} options
 */
/* jshint nomen:false */
define([
    '../map',
    '../svgIcon',
    '../objectUtils',
    './defaultIconsMappingProvider',
    './../../services/utils',
    'services/popupContentGenerator/pointHTMLContentGenerator',
    './routeDrawer',
    'services/popupContentGenerator/wrapable',
    'services/search/nearbySearch',
    'leaflet.markercluster'
], function(
    L,
    SvgIcon,
    objectUtils,
    defaultIconsMappingProvider,
    utils,
    pointHTMLContentGenerator,
    RouteDrawer,
    Wrapable,
    nearbySearch
) {
    'use strict';

    var defaultOptions = {
        background: {
            iconSize: [30, 36],
            iconAnchor: [15, 36],
            icon: 'icon-marker-black',
            noPlainSVG: false
        },
        icon: {
            iconSize: [30, 30],
            iconAnchor: [0, 0],
            icon: 'icon-flag-white',
            noPlainSVG: false
        },
        popupAnchor: [0, -30],
        opacity: 0.8
    };

    var icons = defaultIconsMappingProvider.defaultIcons;
    var singleColorIcons = defaultIconsMappingProvider.singleColorIcons;

    function shouldUseSingleColorIcon(classification) {
        return objectUtils.hasProperty(singleColorIcons, classification);
    }

    function isLowBandwidth(point) {
        return point.a2 !== undefined;
    }

    function hasClassification(point) {
        return objectUtils.hasProperty(point, 'poi') && objectUtils.hasProperty(point.poi, 'classifications');
    }

    function getClassification(point) {
        var classification = 'fallback';
        if (hasClassification(point)) {
            classification = point.poi.classifications.length > 0 ? point.poi.classifications[0].code : 'fallback';
        } else if (isLowBandwidth(point)) {
            classification = point.cst.length > 0 ? point.cst[0].cd : 'fallback';
        }
        return classification;
    }

    /**
     * @ignore
     * If marker layer options enabled support for entry points and addCounterToMainPoint property is set
     * Content with the counter is added
     * @param {Object} point
     * @param {Object} entryPointOptions
     * @return {Object|undefined}
     */
    function getContentForMainMarkerWithEntryPoints(point, entryPointOptions) {
        if (point.entryPoints && entryPointOptions && entryPointOptions.addCounterToMainPoint) {
            return {
                iconSize: [10, 10],
                iconAnchor: [-25, 5],
                content: point.entryPoints.length,
                noPlainSVG: true,
                style: {
                    'color': '#000',
                    'font-weight': 'bold',
                    'font-size': '12px'
                }
            };
        }
        return undefined;
    }

    /**
     * @ignore
     * Returns correct marker options for provided classification and color and entryPoints options
     * @param {String} classification
     * @param {Object} point
     * @param {String} color
     * @param {Object} entryPointsOptions
     * @return {Object}
     */
    function getMarkerIconOptions(classification, point, color, entryPointsOptions) {
        var iconName = icons[classification] || icons.fallback;
        var iconOptions = utils.clone(defaultOptions);
        if (shouldUseSingleColorIcon(classification)) {
            iconOptions.icon.icon = 'icon-' + iconName;
        } else {
            iconOptions.icon.icon = 'icon-' + iconName + '-' + color;
            if (color === 'black') {
                iconOptions.background.icon = 'icon-marker-white';
            }
        }
        iconOptions.content = getContentForMainMarkerWithEntryPoints(point, entryPointsOptions);
        return iconOptions;
    }

    /**
     * @ignore
     * Returns instance of Icon build for correct point classification and color
     * @param {Object} point
     * @param {String} color
     * @param {Object} entryPointsOptions
     * @return {SvgIcon}
     */
    function getDefaultIcon(point, color, entryPointsOptions) {
        var classification, iconOptions;
        classification = getClassification(point);
        iconOptions = getMarkerIconOptions(classification, point, color, entryPointsOptions);
        return new SvgIcon(iconOptions);
    }

    /**
     * @ignore
     * Returns position of the point. Used in marker creation
     * @param {Object} point
     * @return {LatLng}
     */
    function getMarkerPosition(point) {
        var latLngContainer = point;
        if (point.position) {
            latLngContainer = point.position;
        } else if (point.ps) {
            latLngContainer = point.ps.split(' ');
        }
        return utils.convertToLatLng(latLngContainer);
    }

    /**
     * @ignore
     * Calls popupHoverContent generator method if it exists and returns output. Used in marker creation.
     * @param {Object} point
     * @param {Object} options
     * @return {String|undefined}
     */
    function getMarkerPopupHoverContent(point, options) {
        return objectUtils.hasProperty(options, 'popupHoverContent') ? options.popupHoverContent(point) : undefined;
    }

    /**
     * @ignore
     * Calls popupContent generator method if it exists and returns output. Used in marker creation.
     * @param {Object} point
     * @param {Object} options
     * @param {Object} popupGenerator
     * @return {String}
     */
    function getMarkerPopupContent(point, options, popupGenerator) {
        return objectUtils.hasProperty(options, 'popupContent') ?
            options.popupContent(point) :
            popupGenerator(point);
    }

    /**
     * @ignore
     * Returns correct color for markers based on layer options
     * @param {Object} options
     * @param {Boolean} [options.useBlackIcons=false]
     * @return {String}
     */
    function getColor(options) {
        return options.useBlackIcons ? 'black' : 'white';
    }

    /**
     * @ignore
     * Returns svg icon for the typical points
     * @param {Object} point
     * @param {Object} options
     * @param {Boolean} [options.useBlackIcons]
     * @return {SvgIcon}
     */
    function getMarkerIcon(point, options) {
        return objectUtils.hasProperty(options, 'icon') ?
            options.icon(point) :
            getDefaultIcon(point, getColor(options), options.entryPoints);
    }

    /**
     * @ignore
     * Returns svg icon for the entry points
     * @param {Object} point
     * @param {Object} options
     * @return {SvgIcon}
     */
    function getEntryPointMarkerIcon(point, options) {
        var iconOptions;
        if (objectUtils.hasProperty(options, 'icon')) {
            iconOptions = options.icon(point);
        } else {
            iconOptions = utils.clone(defaultOptions);
            iconOptions.icon.icon = 'icon-ic_map_poi_109-' + getColor(options);
            iconOptions.background.icon = 'icon-marker-green';
        }
        return new SvgIcon(iconOptions);
    }

    /**
     * @ignore
     * Calls entry point getEntryPointPopupHoverContent generator method if it exists and returns output. Used in marker
     * creation.
     *
     * @param {Object} entryPoint
     * @param {Object} mainPoint
     * @param {Object} options
     * @return {String}
     */
    function getEntryPointPopupHoverContent(entryPoint, mainPoint, options) {
        return options.entryPoints.popupHoverContent ?
            options.entryPoints.popupHoverContent(entryPoint, mainPoint) :
            undefined;
    }

    /**
     * @ignore
     * Calls entry point popupContent generator method if it exists and returns output. Used in marker creation.
     * @param {Object} entryPoint
     * @param {Object} mainPoint
     * @param {Object} options
     * @param {Function} popupGenerator
     * @return {String}
     */
    function getEntryPointPopupContent(entryPoint, mainPoint, options, popupGenerator) {
        return options.entryPoints.popupContent ?
            options.entryPoints.popupContent(entryPoint, mainPoint) :
            popupGenerator(entryPoint, mainPoint);
    }

    /**
     * @ignore
     * Creates instance of L.polyline for the spider leg line
     * @param {Array} positions
     * @param {Object} options
     * @return {L.polyline}
     */
    function createSpiderLegLine(positions, options) {
        var lineOptions = {
            weight: 1.5,
            color: options.useBlackIcons ? '#ddd' : '#222',
            opacity: 0.5,
            dashArray: '5, 5'
        };
        return L.polyline(positions, lineOptions);
    }

    /**
     * @ignore
     * Creates markers for entry points
     * @param {Array} entryPoints array of entry points data
     * @param {Object} options marker layer options
     * @param {Object} mainPoint main point data object which is passed to popup and title callback for the entry points
     * @param {Function} popupGenerator
     * @return {L.Marker}
     */
    function createMarkersForEntryPoints(entryPoints, options, mainPoint, popupGenerator) {
        return entryPoints.map(function(entryPoint) {
            var entryPointMarkerLayer = L.marker(
                getMarkerPosition(entryPoint), {
                    title: getEntryPointPopupHoverContent(entryPoint, mainPoint, options),
                    popupContent: getEntryPointPopupContent(entryPoint, mainPoint, options, popupGenerator),
                    icon: getEntryPointMarkerIcon(entryPoint, options)
                }
            );
            var popupContent = entryPointMarkerLayer.options.popupContent;

            if (popupContent instanceof Wrapable) {
                entryPointMarkerLayer.bindPopup(popupContent.getHtmlElement());
                entryPointMarkerLayer.on('popupclose', popupContent.wrap.bind(popupContent));
            } else {
                entryPointMarkerLayer.bindPopup(popupContent);
            }

            return entryPointMarkerLayer;
        });
    }

    /**
     * @ignore
     * Draws lines between main point and its entrances
     * @param {Layer} mainMarkerLayer layer of marker which represents main point
     * @param {Array} entryPoints array of layers which represents entry points for the main point
     * @param {Object} options marker layer options
     */
    function drawSpiderLegLines(mainMarkerLayer, entryPoints, options) {
        if (entryPoints.length !== 0) {
            entryPoints.forEach(function(entry) {
                var line = createSpiderLegLine([entry.getLatLng(), mainMarkerLayer.options.point.position], options);
                this._markersWithEntryPointsFeatures.push(line);
                this._map.addLayer(line);
            }.bind(this));
        }
    }

    function configureMarkerToShowEntryPointOnClickIfNeeded(markerLayer, point, options) {
        var markerRemoveHandler;

        if (options.entryPoints !== undefined && point.entryPoints) {
            markerRemoveHandler = function() {
                if (this._lastClickedMarkerWithEntries === markerLayer) {
                    this._removeEntryPoints();
                    this._lastClickedMarkerWithEntries = undefined;
                }
            };

            markerLayer.on('remove', markerRemoveHandler, this);

            markerLayer.on('click', function() {
                var entryPointsMarkers;
                this._removeEntryPoints();

                if (this._lastClickedMarkerWithEntries === markerLayer) {
                    this._lastClickedMarkerWithEntries = undefined;
                    return;
                }

                entryPointsMarkers = createMarkersForEntryPoints(
                    point.entryPoints, options, point, this._entryPointsPopupGenerator
                );
                entryPointsMarkers.forEach(function(entry) {
                    this._markersWithEntryPointsFeatures.push(entry);
                    this._map.addLayer(entry);
                }.bind(this));

                drawSpiderLegLines.call(this, markerLayer, entryPointsMarkers, options);
                this._lastClickedMarkerWithEntries = markerLayer;
            }, this);
        }
    }

    function isSimplePoint(point) {
        return utils.isArray(point) && point.length === 2;
    }

    /**
     * @ignore
     * Builds marker from point data
     * @param {Object} options layer options
     * @param {Boolean} [options.useBlackIcons]
     * @param {Object} point point data to create marker from
     */
    function createMarkerFromPoint(options, point) {
        var markerLayer, popupContent;
        if (isSimplePoint(point)) {
            markerLayer = L.marker(getMarkerPosition(point), {
                point: point
            });
        } else {
            markerLayer = L.marker(getMarkerPosition(point), {
                title: getMarkerPopupHoverContent(point, options),
                popupContent: getMarkerPopupContent(point, options, this._regularPopupGenerator),
                icon: getMarkerIcon(point, options),
                point: point
            });
            popupContent = markerLayer.options.popupContent;

            if (popupContent instanceof Wrapable) {
                markerLayer.bindPopup(popupContent.getHtmlElement());
                markerLayer.on('popupclose', popupContent.wrap.bind(popupContent));
            } else {
                markerLayer.bindPopup(popupContent);
            }
        }

        this._markersLayer.addLayer(markerLayer);

        configureMarkerToShowEntryPointOnClickIfNeeded.call(this, markerLayer, point, options);
    }

    function extendEntryPointsOptions(options) {
        if (this.options.entryPoints && options.entryPoints) {
            L.Util.extend(this.options.entryPoints, options.entryPoints);
            options.entryPoints = this.options.entryPoints;
        }
    }

    function resizeEntryPointMarkerIcon(requestedEntryPoinMarkerPosition, entryPointMarkers, multiplier) {
        function multiply(value) {
            return multiplier * value;
        }
        function updatedElement(element) {
            return Object.assign({}, element, {
                iconSize: element.iconSize.map(multiply),
                iconAnchor: element.iconAnchor.map(multiply)
            });
        }
        entryPointMarkers
            .filter(function(entryPointMarker) {
                return entryPointMarker._latlng &&
                    !utils.isArray(entryPointMarker._latlng) &&
                    entryPointMarker._latlng.equals(requestedEntryPoinMarkerPosition) &&
                    entryPointMarker.options.icon;
            })
            .forEach(function(entryPointMarker) {
                var iconOptions = entryPointMarker.options.icon.options;
                entryPointMarker.refreshIconOptions({
                    background: updatedElement(iconOptions.background),
                    icon: updatedElement(iconOptions.icon)
                });
            });
    }

    function routableClickHandlerFactory(routeContextGetter) {
        return function(mode, point) {
            var routeContext = routeContextGetter();

            if (mode === 'start') {
                routeContext.startPoint(point);
            } else if (mode === 'end') {
                routeContext.endPoint(point);
            }
        };
    }

    function configureActionsForRegularMarkers(markerLayer, isRoutingEnabled, isSearchNearbyEnabled) {
        var actionsForRegularMarkers = null;

        function resizeEntryPoints(value) {
            return function(position) {
                resizeEntryPointMarkerIcon(position, markerLayer._markersWithEntryPointsFeatures, value);
            };
        }

        if (isRoutingEnabled) {
            actionsForRegularMarkers = {};
            actionsForRegularMarkers.routing = {
                routeFrom: true,
                routeTo: true,
                routeToEntryPoint: true,
                routeFromEntryPoint: true,
                onEntryPointMouseover: resizeEntryPoints(2),
                onEntryPointMouseout: resizeEntryPoints(0.5),
                onActionClick: routableClickHandlerFactory(markerLayer._getRoutingContext.bind(markerLayer))
            };
        }

        if (isSearchNearbyEnabled) {
            actionsForRegularMarkers = actionsForRegularMarkers || {};
            actionsForRegularMarkers.search = {
                searchNearby: true,
                onActionClick: function(position) {
                    nearbySearch().center(position).radius(10000).go(function(data) {
                        markerLayer.setMarkersData(data).addMarkers();
                    });
                }
            };
        }

        return actionsForRegularMarkers;
    }

    function configureActionsForEntryPointMarkers(markerLayer, isRoutingEnabled) {
        var actionsForEntryPointMarkers = null;
        if (isRoutingEnabled) {
            actionsForEntryPointMarkers = {};
            actionsForEntryPointMarkers.routing = {
                routeFrom: true,
                routeTo: true,
                onActionClick: routableClickHandlerFactory(markerLayer._getRoutingContext.bind(markerLayer))
            };
        }

        return actionsForEntryPointMarkers;
    }

    function getMarkerName(point) {
        var lowBandwidthName = point.nm ? point.nm : '';
        return point.poi ? point.poi.name : lowBandwidthName;
    }

    function getPointId(point) {
        return String(getMarkerPosition(point)) + getMarkerName(point);
    }

    L.TomTomMarkersLayer = L.FeatureGroup.extend({
        options: {
            disableClusteringAtZoom: 17,
            removeOutsideVisibleBounds: false,
            enableRoutingActions: false,
            enableSearchActions: false
        },
        /**
         * Sets the markers data which will be used by the
         * {{#crossLink "L.TomTomMarkersLayer/addMarkers:method"}}{{/crossLink}} method.
         *
         * This method expects an array of points returned by one of our search calls (e.g. fuzzySearch),
         * or an array of simple points e.g. [[52.005, 25.01], [57.12, 12.06]].
         * ```javascript
         * var map = L.map('map', {key: "<YOUR_API_KEY"});
         * var markers = new L.TomTomMarkersLayer().addTo(map);
         * markers.setMarkersData([[52.005, 25.01], [57.12, 12.06]]);
         * markers.addMarkers();
         * map.fitBounds(markers.getBounds());
         * ``````
         *
         * @chainable
         * @method setMarkersData
         * @param {Array} points Array of markers data to be used by addMarkers method
         * @return {L.TomTomMarkersLayer}
         */
        setMarkersData: function(points) {
            var that = this;
            this._points = points.filter(function(point) {
                var pointId = getPointId(point);
                return that._allPointsIds.indexOf(pointId) === -1;
            });
            this._points.forEach(function(point) {
                var pointId = getPointId(point);
                that._allPointsIds.push(pointId);
            });
            return this;
        },

        /**
         * Adds markers to the layer using the options specified by
         * {{#crossLink "L.TomTomMarkersLayer/setMarkerOptions:method"}}{{/crossLink}} and
         * {{#crossLink "L.TomTomMarkersLayer/setPopupOptions:method"}}{{/crossLink}}.
         *
         * Please remember to add this layer instance into the map after call this method if you want
         * to visualize markers on the map.
         *
         * @chainable
         * @method addMarkers
         * @return {L.TomTomMarkersLayer}
         */
        addMarkers: function() {
            if (this.options.noMarkerClustering) {
                if (!this._markersLayer || !(this._markersLayer instanceof L.FeatureGroup)) {
                    this._markersLayer = new L.FeatureGroup();
                }
            } else if (!this._markersLayer || !(this._markersLayer instanceof L.MarkerClusterGroup)) {
                this._markersLayer = new L.MarkerClusterGroup({
                    chunkedLoading: this.options.chunkedLoading,
                    disableClusteringAtZoom: this.options.disableClusteringAtZoom,
                    maxClusterRadius: this.options.maxClusterRadius,
                    removeOutsideVisibleBounds: this.options.removeOutsideVisibleBounds,
                    showCoverageOnHover: this.options.showCoverageOnHover,
                    spiderfyOnMaxZoom: this.options.spiderfyOnMaxZoom
                });
            }

            this._regularPopupGenerator = pointHTMLContentGenerator({
                name: true,
                address: true,
                coordinates: true,
                poi: true,
                actions: configureActionsForRegularMarkers(this, this.options.enableRoutingActions,
                    this.options.enableSearchActions)
            });
            this._entryPointsPopupGenerator = pointHTMLContentGenerator({
                entryPoint: true,
                address: true,
                coordinates: true,
                actions: configureActionsForEntryPointMarkers(this, this.options.enableRoutingActions)
            });

            this._points.forEach(createMarkerFromPoint.bind(this, this.options));
            this.addLayer(this._markersLayer);
            return this;
        },

        /**
         * @ignore
         * Removes all entry points markers and spiders legs from the map.
         */
        _removeEntryPoints: function() {
            this._markersWithEntryPointsFeatures.forEach(function(feature) {
                feature.remove();
            });
            this._markersWithEntryPointsFeatures = [];
        },
        /**
         * Returns an array of layers which were created by the
         * {{#crossLink "L.TomTomMarkersLayer/addMarkers:method"}}{{/crossLink}}.
         *
         * @method getMarkers
         * @return {Array} Array of marker layers.
         */
        getMarkers: function() {
            if (!this._markersLayer) {
                return [];
            }
            return this._markersLayer.getLayers();
        },

        /**
         * Sets the marker options which will be used by the
         * {{#crossLink "L.TomTomMarkersLayer/addMarkers:method"}}{{/crossLink}} method.
         *
         * @chainable
         * @method setMarkerOptions
         * @param {Object} options Options for marker.
         * @param {Boolean} [options.useBlackIcons=false] If set to true, default icons will be black, else they will
         *     be white.
         * @param {Function} options.icon Function that will be called for every point (passed as the first argument).
         *     Should return an instance of {{#crossLink "L.Icon"}}{{/crossLink}} or any class that extends L.Icon
         *     (e.g. {{#crossLink "L.SvgIcon"}}{{/crossLink}}).
         * @param {Boolean} options.noMarkerClustering If is set to true the markers will not be clustered and the
         *     returned marker's layer will be a {{#crossLink "L.FeatureGroup"}}{{/crossLink}}.
         *     If is set to false the markers will be clustered and the returned marker's layer will be a
         *     {{#crossLink "L.MarkerClusterGroup"}}{{/crossLink}}.
         * @param {Number} [options.disableClusteringAtZoom=17] If marker clustering is turned on which zoom
         *     level should it be turned off.
         * @param {Boolean} [options.removeOutsideVisibleBounds=false] If marker clustering is turned on
         *     determines if markers which are outside of viewport should be removed.
         * @param {Object} options.entryPoints options for viewing entry points of added markers (if not provided entry
         *     points will not be visualized)
         * @param {Boolean} [options.entryPoints.addCounterToMainPoint=false] if set to true, marker with entry points
         *     will have counter
         * @param {Number} options.maxClusterRadius The maximum radius that a cluster will cover from the central
         *     marker (in pixels).
         * @param {Boolean} options.showCoverageOnHover When mouse is over a cluster it shows the bounds of its
         *     markers.
         * @param {Boolean} options.spiderfyOnMaxZoom When user click a cluster at the bottom zoom level it is
         *     spiderfied it.
         * @return {L.TomTomMarkersLayer}
         */
        setMarkerOptions: function(options) {
            extendEntryPointsOptions.call(this, options);
            L.Util.setOptions(this, options);
            return this;
        },
        /**
         * @chainable
         * @method setPopupOptions
         * @param {Object} options Options for popup.
         * @param {Function} options.popupHoverContent Function which will be called for every point (passed as first
         *     argument). It allows you to configure the popup's behavior that appears when the cursor moves over the
         *     marker. Function must return a string.
         * @param {Function} options.popupContent Function which will be called for every point (passed as first
         *     argument). It allows to configure popup content of every marker. This function should return a
         *     {{#crossLink "HTMLElement"}}{{/crossLink}}, {{#crossLink "String"}}{{/crossLink}} or Leaflet Popup
         *     element.
         * @param {Boolean} [options.enableRoutingActions=false] Enables routing actions
         *      ("Route from", "Route to", etc.)
         *      being shown in a popup
         * @param {Boolean} [options.enableSearchActions=false] Enables search actions ("Search nearby")being shown in a
         *      popup
         * @param {Function} options.entryPoints.popupHoverContent function which will be called for each entry point to
         *     create title (visible while hovering over marker). Passed parameters (entry, point)
         * @param {Function} options.entryPoints.popupContent function which will be called for each entry point to
         *     create popup for it. Passed parameters (entry, point)
         * @return {L.TomTomMarkersLayer}
         */
        setPopupOptions: function(options) {
            extendEntryPointsOptions.call(this, options);
            L.Util.setOptions(this, options);
            return this;
        },
        /**
         * Removes all added markers
         *
         * @chainable
         * @method clearLayers
         * @return {L.TomTomMarkersLayer}
         */
        clearLayers: function() {
            if (this._markersLayer) {
                this._markersLayer.clearLayers();
                this._points = null;
                this._allPointsIds = [];
            }
            L.FeatureGroup.prototype.clearLayers.call(this);
            return this;
        },
        onAdd: function(map) {
            if (this.options.enableRoutingActions) {
                this._routingContext = map._routingContext;
                this._routeDrawer.addTo(map);
            }
            this._map = map;
            L.FeatureGroup.prototype.onAdd.call(this, map);
        },
        onRemove: function(map) {
            L.FeatureGroup.prototype.onRemove.call(this, map);
            if (this.options.enableRoutingActions) {
                this._routeDrawer.remove();
            }
            this._map = null;
        },
        _getRoutingContext: function() {
            return this._routingContext;
        }
    });

    L.TomTomMarkersLayer.addInitHook(function() {
        this._allPointsIds = [];
        this._points = [];
        this._markersLayer = undefined;
        this._markersWithEntryPointsFeatures = [];
        this._lastClickedMarkerWithEntries = null;
        this._routeDrawer = new RouteDrawer();
        this._regularPopupGenerator = null;
        this._entryPointsPopupGenerator = null;
    });

    L.tomTomMarkersLayer = function(options) {
        return new L.TomTomMarkersLayer(options);
    };
    return L.tomTomMarkersLayer;

});



// WEBPACK FOOTER //
// ./display/layers/markersLayer.js